import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useEffect, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { formatCalendarDay, toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { de } from 'date-fns/locale';
import { html, nothing, type TemplateResult } from 'lit';
import { of } from 'rxjs';
import {
  BillingPositionTemplateAPI,
  FreeTemplateInformation,
} from '../../billingPositions/api/BillingPositionTemplateAPI.js';
import { GroupsAPI } from '../../groups/api/GroupsAPI.js';
import { Group } from '../../groups/entities/Group.js';
import { School } from '../../groups/entities/School.js';
import { UserListAPI } from '../../users/list/api/UserListAPI.js';
import type { UserListSelectionInterface } from '../../users/list/entities/UserListSelectionInterface.js';
import { SepaAPI } from '../api/SepaAPI.js';
import { SepaExportSettings } from '../entities/SepaExportSettings.js';

type OptionStep = 'date' | 'template' | 'school' | 'group' | null;

const OptionDropdown = (props: {
  icon?: string;
  label: string;
  selectedValue: string | null;
  isOpen: boolean;
  onToggle: () => void;
  content: TemplateResult;
}) => html`
  <div class="flex flex-col">
    <button class="btn btn-lg justify-between w-full ${props.isOpen ? 'rounded-b-none' : ''}" @click=${props.onToggle}>
      <div class="flex items-center gap-2 min-w-0">
        ${props.icon ? html`<span class="text-lg ${props.icon}"></span>` : nothing}
        <span class="truncate">${props.selectedValue || props.label}</span>
      </div>
      <svg
        class="w-4 h-4 transition-transform ${props.isOpen ? 'rotate-180' : ''}"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    ${
      props.isOpen
        ? html`<div class="bg-base-300 rounded-b-box p-2 flex flex-col gap-2">${props.content}</div>`
        : nothing
    }
  </div>
`;

const SepaExportSettingsDialog = stateful(
  (props: {
    userDataInterface: UserListSelectionInterface | null;
    resolve: (sepaExportSettings: SepaExportSettings | null) => void;
    reject: () => void;
  }) => {
    const { value: previewPersons, loading: previewPersonsLoading } = useQuery(
      () => (props.userDataInterface ? UserListAPI.previewSelection(props.userDataInterface)() : of(0)),
      [props.userDataInterface],
    );

    const [settings] = useState(
      new SepaExportSettings({
        userSelectionInterface: props.userDataInterface,
        stichtag: todayAsCalendarDay(),
      }),
    );

    const form = useForm(
      settings,
      {
        validator: null,
      },
      [settings],
    );

    const handleResolve = () => {
      props.resolve(form.document);
    };

    const [billingPositionName, setBillingPositionName] = useState<string | null>(null);
    const [schoolName, setSchoolName] = useState<string | null>(null);
    const [groupNames, setGroupNames] = useState<string | null>(null);

    const [openStep, setOpenStep] = useState<OptionStep>('date');

    /**
     * Stable filter instance for the group picker. Recreating it on every render would unmount &
     * remount the picker (and close its dropdown) on every selection. We only rebuild it when the
     * bp/school selection actually changes.
     */
    const groupFilter = useMemo(
      () => [
        new AdornisFilter({
          searchFields: ['name'],
          aspects: [
            /**
             * The bp/school constraints are forwarded to the server resolver via
             * `customID` StringAspects so the resolver can read them off the filter.
             */
            new StringAspect({
              customID: 'billingPositionName',
              value: form.document.billingPositionName ?? '',
            }),
            new StringAspect({
              customID: 'schoolID',
              value: form.document.schoolID ?? '',
            }),
          ],
        }),
      ],
      [form.document.billingPositionName, form.document.schoolID],
    );

    /**
     * Auto select open dropdown selection
     */
    useEffect(() => {
      requestAnimationFrame(() => {
        const input = document.querySelector<HTMLInputElement>('.sepa-export-settings-dialog input');
        input?.focus();
      });
    }, [openStep]);

    return html`
      <div class="dialog-container sepa-export-settings-dialog">
        ${DialogHeader({ title: 'SEPA-Export-Optionen', close: props.reject, closeLabel: 'Dialog schließen' })}

        <!-- User count info -->
        <div class="flex items-center gap-2">
          ${
            previewPersonsLoading
              ? html`<span class="loading loading-spinner loading-sm"></span>`
              : html`<span class="badge badge-primary badge-lg">${previewPersons ?? 0}</span>`
          }
          <span>Personen ausgewählt</span>
        </div>

        <!-- Option dropdowns -->
        <div class="flex flex-col gap-2">
          ${OptionDropdown({
            icon: 'icon-[material-symbols--calendar-month-outline-rounded]',
            label: 'Stichtag auswählen',
            selectedValue: form.document.stichtag ? formatCalendarDay(form.document.stichtag, 'dd.MM.yyyy') : null,
            isOpen: openStep === 'date',
            onToggle: () => setOpenStep(openStep === 'date' ? null : 'date'),
            content: html`
              <div class="flex flex-col gap-2">
                ${DateInput({
                  value: form.document.stichtag,
                  type: 'date',
                  placeholder: 'Stichtag',
                  placeholderMode: 'prefix',
                  onPicked: date => {
                    if (!date) return;

                    form.document.stichtag = toCalendarDay(date);
                    setOpenStep('template');
                  },
                })}
                <span class="text-sm text-base-content/70">
                  Es werden die Mandate eingezogen, die an diesem Tag laufen. Gebucht wird auf
                  ${
                    form.document.stichtag
                      ? formatCalendarDay(form.document.stichtag, 'MMMM yyyy', { locale: de })
                      : '-'
                  }.
                </span>
              </div>
            `,
          })}
          ${OptionDropdown({
            icon: 'icon-[material-symbols--docs-outline-sharp]',
            label: 'Abrechnungspositions-Template auswählen',
            selectedValue: billingPositionName ?? '',
            isOpen: openStep === 'template',
            onToggle: () => setOpenStep(openStep === 'template' ? null : 'template'),
            content: html`
              ${EntityPickerInput({
                class: FreeTemplateInformation,
                operation: BillingPositionTemplateAPI.getCombined,
                additionalSelectables: [{ _id: 'default', render: 'Alle Abrechnungspositionen' }],
                filter: new AdornisFilter({
                  searchFields: ['name', 'kuerzel'],
                }),
                selectionSet: { name: 1, kuerzel: 1 },
                renderItem: template =>
                  template._id === 'default'
                    ? html`<span>Alle Abrechnungspositionen</span>`
                    : html`
                        <div class="flex gap-2">
                          <span class="badge badge-outline badge-sm">${template.kuerzel}</span>
                          <span>${template.name}</span>
                        </div>
                      `,
                onEntityPicked: selected => {
                  if (selected?._id === 'default') {
                    form.document.billingPositionName = null;
                    setBillingPositionName('Alle Abrechnungspositionen');
                    setOpenStep('school');
                    return;
                  }

                  form.document.billingPositionName = selected ? selected.name : null;
                  setBillingPositionName(selected?.name ?? null);
                  setOpenStep('school');
                },
              })}
            `,
          })}
          ${OptionDropdown({
            icon: 'icon-[material-symbols--school]',
            label: 'Schule auswählen',
            selectedValue: schoolName,
            isOpen: openStep === 'school',
            onToggle: () => setOpenStep(openStep === 'school' ? null : 'school'),
            content: html`
              ${EntityPickerInput({
                class: School,
                operation: GroupsAPI.subscribeGroups,
                additionalSelectables: [{ _id: 'default', render: 'Alle Schulen' }],
                filter: new AdornisFilter({
                  searchFields: ['name'],
                  aspects: [
                    new BooleanAspect({
                      fieldName: 'isSgSchool',
                      value: true,
                    }),
                  ],
                }),
                renderItem: school =>
                  school._id === 'default'
                    ? html`<span>Alle Schulen</span>`
                    : html`
                        <div class="flex gap-2">
                          <span class="badge badge-outline badge-sm">${school.kostenstellePrefix}</span>
                          <span>${school.name}</span>
                        </div>
                      `,
                selectionSet: { name: 1, kostenstellePrefix: 1 },

                onEntityPicked: selected => {
                  if (selected?._id === 'default') {
                    form.document.schoolID = null;
                    setSchoolName('Alle Schulen');
                    setOpenStep('group');
                    return;
                  }
                  form.document.schoolID = selected ? selected._id : null;
                  setSchoolName(selected?.name ?? null);
                  setOpenStep('group');
                },
              })}
            `,
          })}
          ${OptionDropdown({
            icon: 'icon-[material-symbols--groups]',
            label: 'Gruppe auswählen',
            selectedValue: groupNames,
            isOpen: openStep === 'group',
            onToggle: () => setOpenStep(openStep === 'group' ? null : 'group'),
            content: html`
              ${EntityPickerInput({
                class: Group,
                operation: SepaAPI.subscribeActivatedGroupsByBPAndSchool,
                additionalSelectables: [{ _id: 'default', render: 'Alle Gruppen' }],
                filter: groupFilter,
                multi: true,

                renderString: group => (group._id === 'default' ? 'Alle Gruppen' : (group.name ?? '')),
                selectionSet: { name: 1 },
                value: form.document.groupIDs,
                onEntityPicked: selected => {
                  /**
                   * Picking the synthetic `default` entry resets the selection — the export
                   * then targets all groups matching the bp/school filter.
                   */
                  if (selected?.some(g => g._id === 'default')) {
                    form.document.groupIDs = null;
                    setGroupNames('Alle Gruppen');
                    return;
                  }
                  form.document.groupIDs = selected?.length ? selected.map(g => g._id) : null;
                  setGroupNames(selected?.length ? selected.map(g => g.name).join(', ') : null);
                },
              })}
            `,
          })}
        </div>

        <div class="flex flex-col gap-2">
          <label class="flex items-start gap-3 cursor-pointer rounded-lg border border-base-300 p-3">
            <input
              type="radio"
              class="radio radio-sm mt-0.5"
              name="sepa-export-mode"
              .checked=${!form.document.lock}
              @change=${() => (form.document.lock = false)}
            />
            <span class="flex flex-col">
              <span class="font-medium">Nur Vorschau</span>
              <span class="text-sm text-base-content/70">Erzeugt die Dateien und ändert nichts.</span>
            </span>
          </label>
          <label class="flex items-start gap-3 cursor-pointer rounded-lg border border-base-300 p-3">
            <input
              type="radio"
              class="radio radio-sm mt-0.5"
              name="sepa-export-mode"
              .checked=${!!form.document.lock}
              @change=${() => (form.document.lock = true)}
            />
            <span class="flex flex-col">
              <span class="font-medium">Exportieren und sperren</span>
              <span class="text-sm text-base-content/70">
                Erzeugt die Dateien und sperrt alle exportierten Gruppen, bis eine ihrer Buchungen wieder fällig ist.
                Einmalige Mandate gelten danach als abgerechnet; ein zweiter Export für denselben Zeitraum wird
                abgelehnt – auch für Personen dieser Gruppen, die hier nicht ausgewählt sind.
              </span>
            </span>
          </label>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-2">
          <button class="btn btn-primary btn-lg" @click=${handleResolve}>
            ${form.document.lock ? 'Exportieren und sperren' : 'Vorschau erstellen'}
          </button>
        </div>
      </div>
    `;
  },
);

export const openSepaExportSettingsDialog = async (userDataInterface: UserListSelectionInterface) => {
  return showDialog<SepaExportSettings | null>({
    content: (resolve, reject) =>
      SepaExportSettingsDialog({
        userDataInterface,
        resolve,
        reject,
      }),
    modalBoxClasses: 'flex flex-col flex-grow min-w-[600px]',
    modal: true,
  });
};
